<template>
  <screen
    class="uploading-wrapper"
    :class="{ error }"
    :screen-info="screenInfo"
    :can-skip="false"
    :can-leave="false"
    center-titles
  >
    <div class="stars" :class="{ invisible: completed || error }" ref="bg"></div>
    <div class="main-img" ref="body"></div>
    <c-button class="mt-2 z-10" :class="{ disabled: !completed && !error }" @click="onUploadFinish">
      <span>{{ finishBtn }}</span>
    </c-button>
  </screen>
</template>

<script>
import lottie from 'lottie-web';
import { mapActions } from 'vuex';
import ScanProcess from '@/modules/scan-process';

import loadingBg from '@/assets/animations/loading_bg.json';
import loadingBody from '@/assets/animations/loading_body.json';
import { EVENTS as HW_EVENTS } from '@/events/hardware';
import audioPlayer from '@/mixins/audio-player';
import Screen from '@/components/common/Screen.vue';
import CButton from '@/components/common/CButton.vue';

export default {
  name: 'Uploading',
  components: {
    Screen,
    CButton,
  },
  mixins: [audioPlayer],
  mounted() {
    this.playAudio('ms-16-analyzing', true);
    this.$events.on(HW_EVENTS.MOBILE_SCAN_UPLOAD_PROGRESS, this.onProgress);
    this.$events.on(HW_EVENTS.MOBILE_SCAN_UPLOAD_COMPLETE, this.onComplete);
    this.$events.on(HW_EVENTS.MOBILE_SCAN_UPLOAD_ERROR, this.onError);
    this.bgAnimation = lottie.loadAnimation({
      container: this.$refs.bg,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: loadingBg,
    });
    this.bodyAnimation = lottie.loadAnimation({
      container: this.$refs.body,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData: loadingBody,
    });
  },
  beforeDestroy() {
    this.$events.off(HW_EVENTS.MOBILE_SCAN_UPLOAD_PROGRESS, this.onProgress);
    this.$events.off(HW_EVENTS.MOBILE_SCAN_UPLOAD_COMPLETE, this.onComplete);
    this.$events.off(HW_EVENTS.MOBILE_SCAN_UPLOAD_ERROR, this.onError);
    if (this.bgAnimation) {
      this.bgAnimation.destroy();
    }
    if (this.bodyAnimation) {
      this.bodyAnimation.destroy();
    }
  },
  data: () => ({
    progress: 0,
    total: 0,
    completed: false,
    error: false,
  }),
  methods: {
    ...mapActions('user', ['setFreshMobileScan']),
    onProgress({ progress, total }) {
      this.progress = progress;
      this.total = total;
    },
    onComplete() {
      const { tid } = ScanProcess;
      console.log('mobile scan upload complete: ', tid);
      this.playAudio('ms-17-complete', true);
      this.completed = true;
      this.setFreshMobileScan(tid);
    },
    onError(error) {
      console.log('[ERROR] upload error:', error);
      this.error = true;
    },
    onUploadFinish() {
      if (!this.completed && !this.error) return;

      this.$events.emit(HW_EVENTS.CLOSE_MOBILE_SCAN);
      this.$events.goto('HomePage');
    },
  },
  computed: {
    screenInfo() {
      if (this.error) return this.$t('mobileScan.upload_error');

      return this.completed
        ? this.$t('mobileScan.uploading_done')
        : this.$t('mobileScan.uploading');
    },
    percentage() {
      return Math.ceil(this.progress * 100);
    },
    finishBtn() {
      if (this.error) return this.$t('mobileScan.leave');

      return this.completed ? this.$t('mobileScan.finish_btn') : `${this.percentage}%`;
    },
  },
  watch: {
    percentage(nv, ov) {
      if (this.bodyAnimation) {
        this.bodyAnimation.playSegments([Math.ceil(ov * 2.7), Math.ceil(nv * 2.7)], true);
      }
    },
    completed(nv) {
      if (nv) {
        if (this.bodyAnimation) {
          this.bodyAnimation.playSegments([270, 300]);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.uploading-wrapper {
  background: #202020;
  ::v-deep {
    .screen-info {
      @apply text-white font-semibold;
      line-height: 34px;
    }
    .content-container {
      @apply relative;
      top: -10%;
    }
  }
  &.error {
    ::v-deep {
      .screen-info {
        @apply text-base;
      }
    }
  }
}
.stars {
  width: 90%;
  height: 60%;
}
.main-img {
  @apply absolute;
  height: 60%;
}
@media screen and (max-height: 670px) {
  .stars,
  .main-img {
    height: 50%;
  }
}
.disabled {
  @apply opacity-25;
}
</style>
